import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";

const StoreContext = createContext(null);

export function StoreProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [customer, setCustomer] = useState(null); // null=checking, false=guest
  const [cart, setCart] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);

  const refreshCart = useCallback(async () => {
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch (e) {
      /* guest cart will be created lazily */
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/session");
      setCustomer(data.user || false);
    } catch {
      setCustomer(false);
    }
  }, []);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    refreshCart();
    // CRITICAL: If returning from OAuth callback, skip the /me check — AuthCallback handles it first.
    if (!window.location.hash?.includes("session_id=")) {
      checkAuth();
    }
  }, [checkAuth, refreshCart]);

  const addToCart = useCallback(async (productId, variantId, quantity = 1, customization = null) => {
    try {
      const { data } = await api.post("/cart/items", { product_id: productId, variant_id: variantId, quantity, customization });
      setCart(data);
      setCartOpen(true);
      toast.success("Ürün sepete eklendi");
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  }, []);

  const updateItem = useCallback(async (productId, variantId, quantity) => {
    try {
      const { data } = await api.put("/cart/items", { product_id: productId, variant_id: variantId, quantity });
      setCart(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, []);

  const removeItem = useCallback(async (productId, variantId) => {
    const { data } = await api.post("/cart/items/remove", { product_id: productId, variant_id: variantId });
    setCart(data);
  }, []);

  const applyCoupon = useCallback(async (code) => {
    try {
      const { data } = await api.post("/cart/coupon", { code });
      setCart(data);
      toast.success("Kupon uygulandı");
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  }, []);

  const removeCoupon = useCallback(async () => {
    const { data } = await api.delete("/cart/coupon");
    setCart(data);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setCustomer(data);
    await refreshCart();
    return data;
  }, [refreshCart]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setCustomer(data);
    await refreshCart();
    return data;
  }, [refreshCart]);

  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    setCustomer(false);
    await refreshCart();
  }, [refreshCart]);

  return (
    <StoreContext.Provider value={{
      settings, categories, customer, setCustomer, cart, refreshCart,
      cartOpen, setCartOpen, addToCart, updateItem, removeItem,
      applyCoupon, removeCoupon, login, register, logout, checkAuth,
    }}>
      {children}
    </StoreContext.Provider>
  );
}

export const useStore = () => useContext(StoreContext);
